import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search as SearchIcon, MapPin, X, Plus, Sparkles, Globe, Filter, ChevronDown,
} from "lucide-react";

/**
 * PremiumSearch — Aerzteblatt-style faceted search engine for the
 * agency-curated Premium Workers list. Mirrors the look of /app/search
 * (job board) but reads from /api/premium/{listed,facets}.
 */
function FacetGroup({ title, items, selected, onToggle, max = 8 }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, max);
  if (!items || items.length === 0) return null;
  return (
    <div className="border-t border-white/10 pt-5 first:border-t-0 first:pt-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">{title}</div>
      <ul className="space-y-1.5">
        {visible.map((it) => {
          const active = selected.has(it.value);
          return (
            <li key={it.value}>
              <button
                type="button"
                onClick={() => onToggle(it.value)}
                className={`w-full text-start flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-sm transition-colors ${
                  active ? "bg-white text-[#1a1a1c] font-bold" : "text-white/75 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`pf-${title}-${it.value}`}
              >
                <span className="truncate">
                  {active && <X className="inline w-3 h-3 -mt-0.5 me-1" />}
                  {it.value}
                </span>
                <span className={`font-mono text-[11px] tabular-nums shrink-0 ${active ? "text-[#1a1a1c]/70" : "text-white/45"}`}>
                  {it.count}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {items.length > max && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-xs font-medium text-white/55 hover:text-white"
        >
          {expanded ? "Show less ↑" : `Show all ${items.length} →`}
        </button>
      )}
    </div>
  );
}

export default function PremiumSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pre-fill the Where field & applied country filter when arriving from
  // /hire with ?country=… (or from RecruiterDashboard's URL passthrough).
  const initialCountry = searchParams.get("country") || "";
  const [what, setWhat] = useState("");
  const [where, setWhere] = useState(initialCountry);
  const [appliedQ, setAppliedQ] = useState("");
  const [appliedLoc, setAppliedLoc] = useState(initialCountry);
  const [selectedCountries, setSelectedCountries] = useState(new Set());
  const [selectedRoles,     setSelectedRoles]     = useState(new Set());
  const [selectedLangs,     setSelectedLangs]     = useState(new Set());
  const [selectedSkills,    setSelectedSkills]    = useState(new Set());
  const [results, setResults] = useState([]);
  const [facets, setFacets]   = useState({ total: 0, roles: [], countries: [], languages: [], skills: [], agencies: [] });
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (appliedQ)   params.q = appliedQ;
    if (appliedLoc) params.country = appliedLoc;
    // Single-country facet click also drives the country param
    if (!params.country && selectedCountries.size === 1) {
      params.country = [...selectedCountries][0];
    }
    try {
      const [r1, r2] = await Promise.all([
        api.get("/premium/listed", { params }),
        api.get("/premium/facets"),
      ]);
      let rows = r1.data?.items || [];
      // Client-side filter on multi-select facets the backend can't combine
      if (selectedCountries.size > 1) {
        rows = rows.filter((w) => selectedCountries.has(w.country));
      }
      if (selectedRoles.size) {
        rows = rows.filter((w) => selectedRoles.has(w.role));
      }
      if (selectedLangs.size) {
        rows = rows.filter((w) => (w.languages || []).some((l) => selectedLangs.has(l)));
      }
      if (selectedSkills.size) {
        rows = rows.filter((w) => (w.skills || []).some((s) => selectedSkills.has(s)));
      }
      setResults(rows);
      setFacets(r2.data || { total: 0, roles: [], countries: [], languages: [], skills: [], agencies: [] });
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Employer or agency account required.");
        navigate("/hire");
        return;
      }
      toast.error("Could not load Premium workers.");
    } finally {
      setLoading(false);
    }
  }, [appliedQ, appliedLoc, selectedCountries, selectedRoles, selectedLangs, selectedSkills, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSearch = (e) => {
    e?.preventDefault();
    setAppliedQ(what.trim());
    setAppliedLoc(where.trim());
  };

  const toggle = (set, setter) => (value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const totalApproved = facets.total;

  return (
    <div className="bg-[#0c0c0d] text-white" data-testid="premium-search">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        {/* Page heading */}
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-none mb-8 flex items-center gap-3 flex-wrap">
          <span className="text-[#FF9500] num-ticker">{totalApproved}</span>
          <span>Premium workers ready to hire</span>
        </h1>

        {/* Search bar */}
        <form
          onSubmit={onSearch}
          className="bg-white/[0.03] border border-white/10 p-3 grid md:grid-cols-[1fr_1fr_auto] gap-3 mb-10"
          data-testid="premium-search-form"
        >
          <label className="bg-[#141416] border border-white/10 px-4 py-3 flex items-center gap-3">
            <SearchIcon className="w-4 h-4 text-white/50 flex-shrink-0" />
            <input
              value={what}
              onChange={(e) => setWhat(e.target.value)}
              placeholder="What? e.g. welder, nurse, backend engineer…"
              className="bg-transparent text-white outline-none flex-1 placeholder-white/35"
              data-testid="prem-what"
            />
          </label>
          <label className="bg-[#141416] border border-white/10 px-4 py-3 flex items-center gap-3">
            <MapPin className="w-4 h-4 text-white/50 flex-shrink-0" />
            <input
              value={where}
              onChange={(e) => setWhere(e.target.value)}
              placeholder="Where? e.g. Saudi Arabia, India, Qatar…"
              className="bg-transparent text-white outline-none flex-1 placeholder-white/35"
              data-testid="prem-where"
            />
          </label>
          <button
            type="submit"
            className="bg-white text-black hover:bg-white/85 px-8 font-bold flex items-center justify-center gap-2 transition-colors"
            data-testid="prem-search-btn"
          >
            <SearchIcon className="w-4 h-4" /> Find workers
          </button>
        </form>

        {/* Mobile filter toggle */}
        <div className="lg:hidden mb-4">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 border border-white/15 px-4 py-3"
          >
            <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
              <Filter className="w-3.5 h-3.5" /> Filters
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Filters sidebar */}
          <aside className={`bg-white/[0.02] border border-white/10 p-5 self-start lg:sticky lg:top-6 ${filtersOpen ? "" : "hidden lg:block"}`}>
            <h2 className="font-display font-bold text-base mb-4 flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </h2>
            <div className="space-y-5">
              <FacetGroup
                title="Industry"
                items={facets.roles}
                selected={selectedRoles}
                onToggle={toggle(selectedRoles, setSelectedRoles)}
                max={8}
              />
              <FacetGroup
                title="Location"
                items={facets.countries}
                selected={selectedCountries}
                onToggle={toggle(selectedCountries, setSelectedCountries)}
                max={9}
              />
              <FacetGroup
                title="Languages"
                items={facets.languages}
                selected={selectedLangs}
                onToggle={toggle(selectedLangs, setSelectedLangs)}
                max={6}
              />
              <FacetGroup
                title="Skills"
                items={facets.skills}
                selected={selectedSkills}
                onToggle={toggle(selectedSkills, setSelectedSkills)}
                max={10}
              />
            </div>
          </aside>

          {/* Results column */}
          <section data-testid="premium-results">
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
              <div className="font-mono text-[11px] uppercase tracking-widest text-white/50">
                Showing <span className="text-white font-bold">{results.length}</span> of {totalApproved} ·{" "}
                <span className="text-white/65">staff-reviewed</span>
              </div>
              <Link
                to="/agencies/dashboard"
                className="bg-[#FF9500] text-black hover:bg-[#FF9500]/85 font-bold px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
                data-testid="prem-upload-cta"
              >
                <Plus className="w-4 h-4" /> Upload a worker
              </Link>
            </div>

            {loading ? (
              <div className="border border-dashed border-white/15 p-16 text-center text-white/55">Loading Premium list…</div>
            ) : results.length === 0 ? (
              <div className="border border-dashed border-white/15 p-16 text-center text-white/55">
                No workers match your filters yet. Try clearing filters above.
              </div>
            ) : (
              <div className="space-y-3" data-testid="premium-results-list">
                {results.map((w) => (
                  <article
                    key={w.id}
                    className="bg-[#141416] border border-white/10 hover:border-white/30 transition-colors p-5"
                    data-testid={`prem-card-${w.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 flex items-center justify-center font-display font-bold text-lg flex-shrink-0">
                          {w.display_name.split(" ").map((x) => x[0]).join("").slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-display font-bold text-lg leading-tight">{w.display_name}</h3>
                            <span className="px-2 py-0.5 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 font-mono text-[9px] uppercase tracking-widest font-bold">⭐ Premium</span>
                          </div>
                          <div className="text-sm text-white/75 mb-2">{w.headline}</div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/60">
                            <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{w.country}{w.city ? ` · ${w.city}` : ""}</span>
                            <span>· {w.role}</span>
                            <span>· {w.experience_years} yrs</span>
                            {w.expected_salary_usd > 0 && <span>· ${w.expected_salary_usd}/mo</span>}
                            {w.languages?.length > 0 && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{w.languages.join(", ")}</span>}
                          </div>
                          {w.skills?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              {w.skills.slice(0, 8).map((s) => (
                                <span key={s} className="px-2 py-0.5 border border-white/15 text-[10px] font-mono uppercase tracking-widest text-white/75">
                                  {s}
                                </span>
                              ))}
                              {w.skills.length > 8 && <span className="text-[10px] text-white/40 font-mono pt-1">+{w.skills.length - 8}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 space-y-2">
                        {w.iq_score && (
                          <div className="border border-[#8b5cf6]/40 bg-[#8b5cf6]/[0.05] px-3 py-2 text-center" title={`IQ ${w.iq_score} · ${w.iq_percentile}th percentile`}>
                            <div className="font-display font-black text-2xl text-[#8b5cf6]">{w.iq_score}</div>
                            <div className="font-mono text-[9px] uppercase tracking-widest text-[#8b5cf6]/80">IQ</div>
                          </div>
                        )}
                        {w.agency_name ? (
                          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                            via<br />
                            <span className="text-white/80">{w.agency_name}</span>
                          </div>
                        ) : (
                          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                            <Sparkles className="w-3 h-3 inline -mt-0.5 me-1" />Self-applied
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
